import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './BillingHistory.css';

function BillingHistory({ onClose }) {
  const { currentUser, userProfile } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [filter, setFilter] = useState('all'); // all, paid, pending
  const [dateRange, setDateRange] = useState('all'); // all, month, quarter, year

  useEffect(() => {
    fetchInvoices();
  }, [currentUser]);

  const fetchInvoices = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const invoicesRef = collection(db, 'invoices');
      const q = query(
        invoicesRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const invoiceData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setInvoices(invoiceData);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
    setLoading(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const downloadInvoice = async (invoice) => {
    setDownloading(invoice.id);
    try {
      const response = await fetch('/api/invoice/download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invoiceId: invoice.id, invoice })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `LatDiagnostics_Invoice_${invoice.invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        // Fallback: Generate HTML invoice and print
        printInvoice(invoice);
      }
    } catch (error) {
      console.error('Download error:', error);
      // Fallback to print
      printInvoice(invoice);
    }
    setDownloading(null);
  };

  const printInvoice = (invoice) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generateInvoiceHTML(invoice));
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const generateInvoiceHTML = (invoice) => {
    const subtotal = invoice.amount / 1.18; // Remove GST
    const gst = invoice.amount - subtotal;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoiceNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #333; background: #fff; }
          .invoice { max-width: 800px; margin: 0 auto; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px solid #3b82f6; }
          .logo h1 { font-size: 28px; color: #1e3a5f; margin-bottom: 5px; }
          .logo p { color: #666; font-size: 12px; }
          .invoice-info { text-align: right; }
          .invoice-info h2 { font-size: 32px; color: #3b82f6; margin-bottom: 10px; }
          .invoice-info p { color: #666; font-size: 13px; margin: 3px 0; }
          .parties { display: flex; justify-content: space-between; margin-bottom: 40px; }
          .party { width: 45%; }
          .party h3 { font-size: 12px; color: #999; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; }
          .party p { font-size: 14px; margin: 5px 0; }
          .party .name { font-weight: 600; font-size: 16px; color: #1e3a5f; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { background: #1e3a5f; color: white; padding: 15px; text-align: left; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; }
          td { padding: 15px; border-bottom: 1px solid #eee; font-size: 14px; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .totals { width: 350px; margin-left: auto; }
          .totals table { margin-bottom: 0; }
          .totals td { padding: 10px 15px; border: none; }
          .totals tr:last-child { background: #1e3a5f; color: white; font-weight: 600; }
          .totals tr:last-child td { padding: 15px; }
          .status { display: inline-block; padding: 6px 16px; border-radius: 20px; font-size: 12px; font-weight: 600; }
          .status.paid { background: #dcfce7; color: #166534; }
          .status.pending { background: #fef3c7; color: #92400e; }
          .payment-info { background: #f8fafc; padding: 20px; border-radius: 10px; margin: 30px 0; }
          .payment-info h3 { font-size: 14px; color: #1e3a5f; margin-bottom: 15px; }
          .payment-info p { font-size: 13px; color: #666; margin: 5px 0; }
          .footer { text-align: center; margin-top: 50px; padding-top: 20px; border-top: 1px solid #eee; }
          .footer p { font-size: 12px; color: #999; margin: 5px 0; }
          .credits-badge { background: linear-gradient(135deg, #3b82f6, #2563eb); color: white; padding: 4px 12px; border-radius: 15px; font-size: 12px; }
          @media print { body { padding: 20px; } .invoice { max-width: 100%; } }
        </style>
      </head>
      <body>
        <div class="invoice">
          <div class="header">
            <div class="logo">
              <h1>🩺 LatDiagnostics</h1>
              <p>AI-Powered Clinical Decision Support</p>
              <p>GSTIN: 27XXXXX1234X1ZX</p>
            </div>
            <div class="invoice-info">
              <h2>INVOICE</h2>
              <p><strong>${invoice.invoiceNumber}</strong></p>
              <p>Date: ${formatDate(invoice.createdAt)}</p>
              <p>Due Date: ${formatDate(invoice.createdAt)}</p>
              <p><span class="status paid">PAID</span></p>
            </div>
          </div>
          
          <div class="parties">
            <div class="party">
              <h3>Billed From</h3>
              <p class="name">LatDiagnostics Pvt. Ltd.</p>
              <p>Mumbai, Maharashtra</p>
              <p>India - 400001</p>
              <p>support@latdiagnostics.com</p>
              <p>+91 73789 57088</p>
            </div>
            <div class="party">
              <h3>Billed To</h3>
              <p class="name">${invoice.customerName || 'Customer'}</p>
              <p>${invoice.customerPhone || ''}</p>
              <p>${invoice.customerEmail || ''}</p>
              <p>${invoice.customerOrg || ''}</p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th class="text-center">Qty</th>
                <th class="text-right">Rate</th>
                <th class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>${invoice.planName} Plan</strong> ${invoice.billingCycle === 'annual' ? '(Annual)' : '(Monthly)'}<br>
                  <span style="color: #666; font-size: 12px;">AI Diagnosis Credits - Valid for ${invoice.billingCycle === 'annual' ? '12 months' : '30 days'}</span>
                </td>
                <td class="text-center"><span class="credits-badge">${invoice.credits} Credits</span></td>
                <td class="text-right">${formatCurrency(subtotal)}</td>
                <td class="text-right">${formatCurrency(subtotal)}</td>
              </tr>
            </tbody>
          </table>
          
          <div class="totals">
            <table>
              <tr>
                <td>Subtotal</td>
                <td class="text-right">${formatCurrency(subtotal)}</td>
              </tr>
              <tr>
                <td>CGST (9%)</td>
                <td class="text-right">${formatCurrency(gst / 2)}</td>
              </tr>
              <tr>
                <td>SGST (9%)</td>
                <td class="text-right">${formatCurrency(gst / 2)}</td>
              </tr>
              <tr>
                <td>Total</td>
                <td class="text-right">${formatCurrency(invoice.amount)}</td>
              </tr>
            </table>
          </div>
          
          <div class="payment-info">
            <h3>Payment Information</h3>
            <p><strong>Payment Method:</strong> ${invoice.paymentMethod || 'Online Payment'}</p>
            <p><strong>Transaction ID:</strong> ${invoice.paymentId || 'N/A'}</p>
            <p><strong>Payment Date:</strong> ${formatDateTime(invoice.createdAt)}</p>
            <p><strong>Status:</strong> <span class="status paid">PAID</span></p>
          </div>
          
          <div class="footer">
            <p>Thank you for choosing LatDiagnostics!</p>
            <p>This is a computer-generated invoice and does not require a signature.</p>
            <p>For any queries, contact support@latdiagnostics.com</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const filterInvoices = () => {
    let filtered = [...invoices];
    
    // Filter by status
    if (filter !== 'all') {
      filtered = filtered.filter(inv => inv.status === filter);
    }
    
    // Filter by date range
    if (dateRange !== 'all') {
      const now = new Date();
      let startDate;
      
      switch (dateRange) {
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'quarter':
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          break;
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = null;
      }
      
      if (startDate) {
        filtered = filtered.filter(inv => {
          const invDate = inv.createdAt?.toDate ? inv.createdAt.toDate() : new Date(inv.createdAt);
          return invDate >= startDate;
        });
      }
    }
    
    return filtered;
  };

  const getTotalSpent = () => {
    return invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  };

  const getTotalCredits = () => {
    return invoices.reduce((sum, inv) => sum + (inv.credits || 0), 0);
  };

  const filteredInvoices = filterInvoices();

  return (
    <div className="billing-overlay">
      <div className="billing-panel">
        {/* Header */}
        <div className="billing-header">
          <div className="billing-title">
            <span className="billing-icon">📄</span>
            <h2>Billing & Invoices</h2>
          </div>
          <button className="billing-close" onClick={onClose}>✕</button>
        </div>

        {/* Stats */}
        <div className="billing-stats">
          <div className="stat-box">
            <div className="stat-value">{invoices.length}</div>
            <div className="stat-label">Total Invoices</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{formatCurrency(getTotalSpent())}</div>
            <div className="stat-label">Total Spent</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{getTotalCredits()}</div>
            <div className="stat-label">Credits Purchased</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{userProfile?.creditsRemaining || 0}</div>
            <div className="stat-label">Current Balance</div>
          </div>
        </div>

        {/* Filters */}
        <div className="billing-filters">
          <div className="filter-group">
            <label>Status</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Period</label>
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="all">All Time</option>
              <option value="month">This Month</option>
              <option value="quarter">Last 3 Months</option>
              <option value="year">This Year</option>
            </select>
          </div>
          <button className="refresh-btn" onClick={fetchInvoices}>
            🔄 Refresh
          </button>
        </div>

        {/* Invoice List */}
        <div className="billing-content">
          {loading ? (
            <div className="billing-loading">
              <div className="spinner"></div>
              <p>Loading invoices...</p>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="billing-empty">
              <div className="empty-icon">📭</div>
              <h3>No Invoices Found</h3>
              <p>Your purchase history will appear here after you buy a plan.</p>
            </div>
          ) : (
            <div className="invoice-list">
              {filteredInvoices.map((invoice) => (
                <div 
                  key={invoice.id} 
                  className={`invoice-card ${selectedInvoice?.id === invoice.id ? 'selected' : ''}`}
                  onClick={() => setSelectedInvoice(selectedInvoice?.id === invoice.id ? null : invoice)}
                >
                  <div className="invoice-main">
                    <div className="invoice-left">
                      <div className="invoice-number">{invoice.invoiceNumber}</div>
                      <div className="invoice-date">{formatDate(invoice.createdAt)}</div>
                    </div>
                    <div className="invoice-center">
                      <div className="invoice-plan">{invoice.planName} Plan</div>
                      <div className="invoice-credits">{invoice.credits} Credits</div>
                    </div>
                    <div className="invoice-right">
                      <div className="invoice-amount">{formatCurrency(invoice.amount)}</div>
                      <span className={`invoice-status ${invoice.status || 'paid'}`}>
                        {invoice.status === 'pending' ? 'Pending' : 'Paid'}
                      </span>
                    </div>
                    <div className="invoice-actions">
                      <button 
                        className="action-btn view"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedInvoice(invoice);
                        }}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button 
                        className="action-btn download"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadInvoice(invoice);
                        }}
                        disabled={downloading === invoice.id}
                        title="Download PDF"
                      >
                        {downloading === invoice.id ? '⏳' : '📥'}
                      </button>
                      <button 
                        className="action-btn print"
                        onClick={(e) => {
                          e.stopPropagation();
                          printInvoice(invoice);
                        }}
                        title="Print"
                      >
                        🖨️
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {selectedInvoice?.id === invoice.id && (
                    <div className="invoice-details">
                      <div className="details-grid">
                        <div className="detail-item">
                          <span className="detail-label">Invoice Number</span>
                          <span className="detail-value">{invoice.invoiceNumber}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Date</span>
                          <span className="detail-value">{formatDateTime(invoice.createdAt)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Plan</span>
                          <span className="detail-value">{invoice.planName} ({invoice.billingCycle})</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Credits</span>
                          <span className="detail-value">{invoice.credits}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Subtotal</span>
                          <span className="detail-value">{formatCurrency(invoice.amount / 1.18)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">GST (18%)</span>
                          <span className="detail-value">{formatCurrency(invoice.amount - invoice.amount / 1.18)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Total Amount</span>
                          <span className="detail-value highlight">{formatCurrency(invoice.amount)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Payment Method</span>
                          <span className="detail-value">{invoice.paymentMethod || 'Razorpay'}</span>
                        </div>
                        <div className="detail-item full-width">
                          <span className="detail-label">Transaction ID</span>
                          <span className="detail-value mono">{invoice.paymentId || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="billing-footer">
          <p>For billing queries, contact support@latdiagnostics.com</p>
        </div>
      </div>
    </div>
  );
}

export default BillingHistory;
